import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type {
    ITrialBalanceService,
    TrialBalanceReadOptions,
} from './ITrialBalanceService';
import { TrialBalanceEndpoints } from './trialBalanceEndpoints';
import { mapTrialBalance, type TrialBalanceWire } from './trialBalanceMapping';
import type { GetTrialBalanceResponse } from './responses';

type TrialBalanceServiceOptions = {
    baseUrl: string;
};

/**
 * A body this client could not read as a trial balance is contract drift, not a
 * figure to guess at. Mirrors `StatementsService`'s `UNMAPPABLE_STATEMENT` and
 * `AccountsService`'s `UNMAPPABLE_ACCOUNT`.
 *
 * It matters more here than on either of those. The screen's whole purpose is
 * the totals row and its balanced/not-balanced verdict; a partially-read trial
 * balance rendered as though it were the position is the single most convincing
 * wrong answer this product can give. The page's error-with-retry state is the
 * correct outcome.
 */
const UNMAPPABLE_TRIAL_BALANCE: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message: 'The server returned a trial balance this client cannot read.',
        code: 'UNMAPPABLE_TRIAL_BALANCE',
    },
};

const DEFAULT_OPTIONS: TrialBalanceReadOptions = {
    locale: 'en',
    excludeClosing: false,
};

/**
 * The live trial-balance client (bos-api).
 *
 * Maps at the boundary via `trialBalanceMapping`, so nothing downstream sees a
 * wire shape and the server's field names are confined to that one file.
 *
 * What this service deliberately does **not** do:
 *
 * - It does not sum anything. Both totals arrive computed.
 * - It does not decide which column a figure belongs in. Every row arrives with
 *   its debit and its credit already placed, which is why a contra account needs
 *   no special case.
 * - It does not compute `balanced`. That verdict is the server's.
 * - It does not filter rows. It has nothing to filter: the server already drops
 *   every account whose balance is nil, and offers no way to ask for them.
 */
export class TrialBalanceService implements ITrialBalanceService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: TrialBalanceServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getTrialBalance(
        asOf: string,
        options: TrialBalanceReadOptions = DEFAULT_OPTIONS,
    ): Promise<ApiResponse<GetTrialBalanceResponse>> {
        const result = await makeApiRequest<TrialBalanceWire>(
            this.client.get(
                TrialBalanceEndpoints.trialBalance({
                    asOf,
                    locale: options.locale,
                    excludeClosing: options.excludeClosing,
                }),
            ),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapTrialBalance(result.response);
        if (!mapped) {
            return UNMAPPABLE_TRIAL_BALANCE;
        }
        return { success: true, response: mapped };
    }
}
